import React, { createRef, memo } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { customAudit, getPagedRouteHistory } from "../../../redux/actions/index";
import { Select } from "antd";
import { calculateBearingText, calculateMileageIncreasing, parseRoutePositionData } from "../../util/Geometry";
import { getAbsoluteTimestamp } from "../../util/PlaylistUtils";
import memoizeOne from "memoize-one";
import OBCSearchInput from "components/OBC/OBCSearchInput";
import OBCSpinner from "components/util/OBC";
import AutoSizer from "react-virtualized-auto-sizer";
import { areEqual, FixedSizeList } from "react-window";
import { setHideLowQualitySessions } from "redux/actions/sessionActions";
import { routeGeomSelector } from "redux/selectors";
import RouteHistoryListItem from "./RouteHistoryListItem";

const SESSION_ITEM_HEIGHT = 90;

const RouteHistoryListRow = memo(({ index, style, data }) => {
    const point = data.points[index];

    let currentItem = false;

    if (data.sessionID === point.session_id && data.sourceIndex === _.get(point, ["stream_info", "source"])) {
        currentItem = true;
    }

    return (
        <RouteHistoryListItem
            key={`${point.session_id}.${_.get(point, ["stream_info", "source"], 0)}`}
            sessionID={point.session_id}
            point={point}
            currentItem={currentItem}
            videoKey={data.videoKey}
            availableTags={data.tags}
            sessionSearch={data.filters.sessionSearch ?? ""}
            tagFilter={data.filters.tag}
            style={style}
        />
    );
}, areEqual);

class RouteHistoryList extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            filters: {
                direction: null,
                track: null,
                bearing: null,
                tag: null,
                day: null,
                sessionSearch: null,
            },
            checkedTimestamp: null,
            position: null,
            filteredPoints: [],
            scrollRef: null,
        };

        this.scrollRef = createRef();
    }

    componentDidMount = () => {
        this.componentDidUpdate({});
        this.props.dispatch(
            customAudit("history_tab_opened", {
                sessionID: this.props.sessionID,
                videoTimestamp: this.props.timestamp,
            }),
        );
    };

    getPagedHistory = () => {
        if (!this.props.currentRoutePosition) {
            return null;
        }

        const routePosition = this.props.currentRoutePosition;
        const currentLocation = parseRoutePositionData(routePosition, this.props.routeLocationSystemID);

        if (currentLocation && currentLocation.route) {
            this.props.dispatch(getPagedRouteHistory(currentLocation, false, this.props.qaFilter));
        }
    };

    debouncedGetHistory = _.debounce(this.getPagedHistory, 1500, { leading: true });

    componentDidUpdate(prevProps, prevState) {
        const isRailImages = this.props.railInspectionImageTimestamp ? true : false;
        let isActive = false;

        if (this.props.visible && this.props.playlistPosition.playerState !== "playing") {
            if (isRailImages) {
                isActive = this.props.railInspectionImageTimestamp;
            } else {
                isActive = this.props.timestamp;
            }
        }

        if (isActive && !isRailImages && this.state.checkedTimestamp !== this.props.timestamp) {
            this.setState({ checkedTimestamp: this.props.timestamp });
            this.debouncedGetHistory();
        }

        if (isActive && isRailImages && this.props.requestedHistoryPosition !== this.currentPosition) {
            this.debouncedGetHistory();
        }

        if (prevState && prevState.filteredPoints !== this.state.filteredPoints) {
            this.scrollToSelectedItem();
        }

        if (prevProps && this.props.qaFilter !== prevProps.qaFilter && isActive) {
            this.debouncedGetHistory();
        }

        this.updateFilteredPoints();
    }

    updateFilteredPoints = () => {
        const filteredPoints = this.filteredPoints(this.props.consolidatedHistory, this.state.filters);

        if (this.state.filteredPoints !== filteredPoints) {
            this.setState({ filteredPoints }, () => {
                if (this.scrollRef?.current) {
                    this.scrollToSelectedItem(this.scrollRef.current);
                }
            });
        }
    };

    scrollToSelectedItem = (ref = null) => {
        const currentItemIndex = _.findIndex(this.state.filteredPoints, { session_id: this.props.sessionID });

        if (currentItemIndex > -1) {
            if (ref) {
                ref.scrollToItem(currentItemIndex, "center");
            } else if (this.scrollRef?.current) {
                this.scrollRef.current.scrollToItem(currentItemIndex, "center");
            }
        }
    };

    currentPosition = () => {
        const routePos = this.props.currentRoutePosition;

        if (!_.isNil(_.get(routePos, ["position"]))) {
            return routePos.position.toFixed(3).toString();
        }
    };

    updateFilter = (key, value) => {
        this.setState((prevState) => ({
            filters: {
                ...prevState.filters,
                [key]: value,
            },
        }));
    };

    renderTrackFilterOptions = () => {
        return _.uniq(_.map(this.props.consolidatedHistory, "sub_position"))
            .sort()
            .map((trackID) => {
                return (
                    <Select.Option
                        key={trackID}
                        value={trackID}>
                        {trackID}
                    </Select.Option>
                );
            });
    };

    getBearingOptions = () => {
        return _.uniq(
            this.props.consolidatedHistory.map((session) => {
                return calculateBearingText(session.bearing);
            }),
        ).map((bearing) => {
            return (
                <Select.Option
                    value={bearing}
                    key={bearing}>
                    {bearing}
                </Select.Option>
            );
        });
    };

    renderTagFilterOptions = () => {
        const filteredTags = this.props.tags.filter((tag) => {
            return (
                _.findIndex(this.props.workspaceTags, (workspaceTag) => {
                    return workspaceTag.tag === tag;
                }) > -1
            );
        });

        return _.map(filteredTags, (tag) => {
            if (!["day", "night"].includes(tag.toLowerCase())) {
                return (
                    <Select.Option
                        key={tag}
                        value={tag}>
                        {tag}
                    </Select.Option>
                );
            }
        });
    };

    renderDayNightFilterOptions = () => {
        return _.map(this.props.tags, (tag) => {
            if (["day", "night"].includes(tag.toLowerCase())) {
                return (
                    <Select.Option
                        key={tag}
                        value={tag}>
                        {tag}
                    </Select.Option>
                );
            }
        });
    };

    filteredPoints = memoizeOne((history, filters) => {
        return history.filter((session) => {
            if (!session) return false;

            if (filters.direction && !_.get(session, ["session_tags"], []).includes(filters.direction)) {
                return false;
            }

            if (filters.day && session["session_tags"] && !session["session_tags"].includes(filters.day)) {
                return false;
            }

            if (filters.track && filters.track !== session.sub_position) {
                return false;
            }

            if (filters.bearing && filters.bearing !== calculateBearingText(session.bearing)) {
                return false;
            }

            if (filters.tag && session["session_tags"] && !session["session_tags"].includes(filters.tag)) {
                return false;
            }

            if (filters.sessionSearch) {
                const lcSessionSearch = filters.sessionSearch.toLowerCase();
                const lcSessionName = _.get(session, ["name"], "").toLowerCase();

                if (!lcSessionName.includes(lcSessionSearch) && !String(session.session_id).includes(lcSessionSearch)) {
                    return false;
                }
            }

            return true;
        });
    });

    render() {
        let empty = !this.props.visible || this.props.consolidatedHistory.length === 0;

        let paused = this.props.playlistPosition.playerState === "paused" || !this.props.playlistPosition.playerState;

        if (this.props.autoScrollActive) {
            paused = false;
        }

        let content;

        if (paused && !empty && !this.props.fetchingHistory && this.props.requestedHistoryPosition === this.currentPosition()) {
            if (this.state.filteredPoints.length === 0) {
                content = (
                    <div className="InformationPanel">
                        <h2>No route history for selected filters, please adjust filters</h2>
                    </div>
                );
            } else {
                const itemData = {
                    points: this.state.filteredPoints,
                    sessionID: this.props.sessionID,
                    sourceIndex: this.props.sourceIndex,
                    filters: this.state.filters,
                    tags: this.props.tags,
                    videoKey: this.props.videoKey,
                };

                content = (
                    <AutoSizer
                        className="HistoryAutoSizer"
                        onResize={this.sessionListResize}>
                        {({ height, width }) => (
                            <FixedSizeList
                                height={height}
                                width={width}
                                style={{ paddingBottom: 10 }}
                                itemSize={SESSION_ITEM_HEIGHT}
                                ref={(ref) => {
                                    this.scrollRef = ref;
                                    this.scrollToSelectedItem(ref);
                                }}
                                itemCount={this.state.filteredPoints.length}
                                itemData={itemData}>
                                {RouteHistoryListRow}
                            </FixedSizeList>
                        )}
                    </AutoSizer>
                );
            }
        } else if (!paused) {
            content = (
                <div className="InformationPanel">
                    <h2>Pause the player to view route history</h2>
                </div>
            );
        } else if (!this.props.consolidatedHistory.length && !this.props.fetchingHistory) {
            content = (
                <div className="InformationPanel">
                    <h2>No route history matching the current filters for the current position</h2>
                </div>
            );
        } else if (!this.props.sessionID) {
            content = (
                <div className="InformationPanel">
                    <h2>No route selected</h2>
                </div>
            );
        } else {
            content = (
                <div className="LoadingSpinner">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            );
        }

        return (
            <div className="RouteHistoryTab">
                <div className="HistoryListFilterContainer">
                    <div className="LeftCol">
                        <div className="HistoryListFilterItem">
                            <span className="label">Bearing:</span>
                            <Select
                                id="BearingSelect"
                                className="HistoryListFilter"
                                dropdownMatchSelectWidth={false}
                                size="small"
                                value={this.state.filters.bearing}
                                onChange={(value) => this.updateFilter("bearing", value)}>
                                <Select.Option value={null}>All</Select.Option>
                                {this.getBearingOptions()}
                            </Select>
                        </div>

                        <div className="HistoryListFilterItem">
                            <span className="label">Day Time:</span>
                            <Select
                                id="SessionTimeSelect"
                                className="HistoryListFilter"
                                size="small"
                                dropdownMatchSelectWidth={false}
                                value={this.state.filters.day}
                                onChange={(value) => this.updateFilter("day", value)}>
                                <Select.Option value={null}>Any</Select.Option>
                                {this.renderDayNightFilterOptions()}
                            </Select>
                        </div>

                        <div className="HistoryListFilterItem">
                            <span className="label">Video Direction:</span>
                            <Select
                                id="DirectionSelect"
                                className="HistoryListFilter"
                                size="small"
                                dropdownMatchSelectWidth={false}
                                value={this.state.filters.direction}
                                onChange={(value) => this.updateFilter("direction", value)}>
                                <Select.Option value={null}>All</Select.Option>
                                <Select.Option value="Forward">Forward</Select.Option>
                                <Select.Option value="Backward">Backward</Select.Option>
                            </Select>
                        </div>

                        <div className="HistoryListFilterItem">
                            <span className="label">Track ID:</span>
                            <Select
                                id="TrackSelect"
                                className="HistoryListFilter"
                                size="small"
                                dropdownMatchSelectWidth={false}
                                value={this.state.filters.track}
                                onChange={(value) => this.updateFilter("track", value)}>
                                <Select.Option value={null}>All</Select.Option>
                                {this.renderTrackFilterOptions()}
                            </Select>
                        </div>
                        <div className="HistoryListFilterItem">
                            <span className="label">Tags:</span>
                            <Select
                                id="TagsSelect"
                                className="HistoryListFilter"
                                size="small"
                                dropdownMatchSelectWidth={false}
                                value={this.state.filters.tag}
                                onChange={(value) => this.updateFilter("tag", value)}>
                                <Select.Option value={null}>All</Select.Option>
                                {this.renderTagFilterOptions()}
                            </Select>
                        </div>
                        {this.props.userConfig.super_admin && (
                            <div className="HistoryListFilterItem">
                                <span className="label">QA Filter:</span>
                                <Select
                                    id="QaSelect"
                                    className="HistoryListFilter"
                                    size="small"
                                    dropdownMatchSelectWidth={false}
                                    value={this.props.qaFilter ? "filtered" : "all"}
                                    onChange={(value) => this.props.dispatch(setHideLowQualitySessions(value === "filtered"))}>
                                    <Select.Option value="all">All</Select.Option>
                                    <Select.Option value="filtered">QA Filtered</Select.Option>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div className="RightCol">
                        <div
                            className="sessionSearch"
                            id="intro-tour-session-search">
                            <OBCSearchInput
                                id="HistorySessionSearch"
                                value={this.state.filters.sessionSearch ?? ""}
                                onChange={(value) => this.updateFilter("sessionSearch", value.length ? value : null)}
                                placeholder="Search for a session"
                                allowClear
                            />
                        </div>
                    </div>
                </div>
                <div className="RouteHistoryListContainer">{content}</div>
            </div>
        );
    }
}

const uniqHistory = memoizeOne((history) => {
    return _.orderBy(history, ["timestamp"], ["desc"]);
});

const EMPTY_ARRAY = [];

const mapStateToProps = (state, ownProps) => {
    const sourceIndex = state.playlist.position.sourceIndex;
    const video = _.get(state.playlist.data, ["video", sourceIndex]);
    const currentPlaylistIndex = state.playlist.position.currentIndex;

    const routeID = _.get(state, ["playlist", "data", "routeID"], null);

    const isVideo = state.playlist.position.isVideo;
    const playlist = isVideo ? _.get(state.playlist.data, ["video", sourceIndex], EMPTY_ARRAY) : state.playlist.data.image;
    const index = state.playlist.position.currentIndex;
    const offset = state.playlist.position.currentTimeOffset || 0;
    const timestamp = getAbsoluteTimestamp(routeID, playlist, index, isVideo, offset);

    let videoKey = null;

    if (routeID !== null && state.sessions[routeID]) {
        if (video && video.length && currentPlaylistIndex !== undefined && video[currentPlaylistIndex]) {
            videoKey = video[currentPlaylistIndex][0];
        }
    }

    const consolidatedHistory = uniqHistory(_.get(state.playlist.consolidatedHistory, ["history"], EMPTY_ARRAY));

    let tags = EMPTY_ARRAY;
    if (consolidatedHistory.length > 0) {
        _.forEach(consolidatedHistory, (session) => {
            _.forEach(session.session_tags, (tag) => {
                if (!tags.includes(tag) && !["Backward", "Forward"].includes(tag)) {
                    tags.push(tag);
                }
            });
        });
    }

    const currentSessionTags = _.get(state.sessions[state.playlist.data.routeID], "tags", []);
    const sessionIsBackward = _.includes(currentSessionTags, "Backward");

    let mileageIncreasing = false;
    if (ownProps.visible && state.playlist.position.playerState !== "playing") {
        mileageIncreasing = calculateMileageIncreasing(state.playlist.position.playlistDistance, state.playlist.data.route_locations_v2, sessionIsBackward);
    }

    return {
        workspaceTags: state.sessionTags,
        timestamp,
        videoKey,
        consolidatedHistory: consolidatedHistory,
        requestedHistoryPosition: _.get(state.playlist.consolidatedHistory, ["requestedPosition"], 0),
        playlistPosition: state.playlist.position,
        sessionIDs: state.sessionList,
        fetchingHistory: _.get(state.playlist.consolidatedHistory, ["fetching"], false),
        currentSourceIndex: sourceIndex,
        tags: tags.sort(),
        railInspectionImageTimestamp: state.railInspection.selectedRailInspectionImage.timestamp,
        sessionID: routeID,
        sourceIndex,
        routeLocationSystemID: state.playlist.data.system_id,
        userConfig: state.userDetails.userConfig,
        autoScrollActive: state.railInspection.autoScrollActive,
        qaFilter: state.sessionListFilters.filter_qa_tags,
        routeGeometryData: routeGeomSelector(state),
        currentRoutePosition: state.playlist.position.routePosition,
        currentSessionMileageIncreasing: mileageIncreasing,
    };
};

export default connect(mapStateToProps)(RouteHistoryList);
